"use client"

import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from "react"
import { pushGtmEvent } from "@lib/gtm"
import { Icon } from "@/shared/ui/icon"
import {
  businessScenarios,
  type BusinessScenario,
  type ScenarioTone,
} from "../_data/business-scenarios"
import { AtlasAgentRun, AtlasInputBar, type AtlasChatStep } from "./AtlasChatMockup"
import { useAtlasReveal } from "./useAtlasReveal"

const scenarioRevealTargets = [{ selector: ".scenario-intro", y: 28 }] as const

const toneClassNames: Record<ScenarioTone, string> = {
  positive: "bg-emerald-400",
  warning: "bg-amber-400",
  neutral: "bg-violet-400",
}

function ScenarioContext({ scenario }: { scenario: BusinessScenario }) {
  return (
    <article className="grid h-full grid-rows-[minmax(18rem,1fr)_auto] overflow-hidden bg-white text-slate-950 dark:bg-[#191b20] dark:text-white">
      <div className="flex min-h-0 flex-col">
        <header className="flex items-center gap-3 border-b border-slate-200 px-5 py-4 dark:border-white/10 sm:px-7">
          <span className="flex size-9 items-center justify-center rounded-xl bg-violet-100 text-violet-700 dark:bg-violet-400/15 dark:text-violet-300">
            <Icon name={scenario.icon} className="size-[1.125rem]" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold sm:text-base">{scenario.role}</h3>
            <p className="font-mono text-2xs text-slate-400 dark:text-white/40 sm:text-xs">
              Рабочий запрос
            </p>
          </div>
        </header>

        <div className="flex flex-1 flex-col justify-center px-5 py-8 sm:px-8 sm:py-10 lg:px-9">
          <blockquote className="border-l-2 border-violet-500 pl-5 text-lead text-slate-900 dark:text-white/90">
            «{scenario.prompt}»
          </blockquote>

          <div className="mt-8 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 border-t border-slate-200 pt-5 dark:border-white/10">
            <span className="flex size-7 items-center justify-center rounded-full bg-slate-950 text-white dark:bg-white dark:text-slate-950">
              <Icon name="arrow-right-linear" className="size-3.5" />
            </span>
            <div className="min-w-0">
              <p className="font-mono text-2xs text-slate-400 dark:text-white/40">
                На выходе
              </p>
              <p className="mt-0.5 truncate text-xs font-semibold text-slate-700 dark:text-white/75">
                {scenario.artifact.title}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200 bg-slate-50/80 p-5 dark:border-white/10 dark:bg-white/[0.025] sm:p-7 lg:p-8">
        <div className="grid gap-7">
          <div>
            <h4 className="text-sm font-semibold text-slate-950 dark:text-white">
              Источники
            </h4>
            <ul className="mt-3 divide-y divide-slate-200 border-y border-slate-200 dark:divide-white/10 dark:border-white/10">
              {scenario.sources.map((source) => (
                <li
                  key={source}
                  className="flex items-center gap-3 py-3 text-sm font-medium text-slate-600 dark:text-white/60"
                >
                  <Icon
                    name="database-linear"
                    className="size-4 shrink-0 text-violet-500"
                  />
                  {source}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-950 dark:text-white">
              Как работает
            </h4>
            <ol className="mt-3 grid gap-2.5">
              {scenario.steps.map((step, index) => (
                <li
                  key={step}
                  className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2 text-sm text-slate-600 dark:text-white/55"
                >
                  <span className="type-numeric pt-0.5 text-2xs text-violet-600 dark:text-violet-300">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </article>
  )
}

function AtlasRun({ scenario }: { scenario: BusinessScenario }) {
  const steps: readonly AtlasChatStep[] = scenario.agentSteps.map((step, index) => ({
    ...step,
    time: `${8 + index * 4}.${index + 1} c`,
  }))

  return (
    <article className="atlas-chat-shell flex h-full flex-col bg-[#171717] text-white">
      <div className="flex flex-1 flex-col p-4 sm:p-6 lg:p-7">
        <div className="mb-4 grid gap-2 rounded-2xl border border-white/10 bg-white/[0.035] p-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center sm:p-4">
          <div className="flex items-center gap-2">
            {/* <span className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-bold text-slate-950">
              <Icon name="radar-bold-duotone" className="size-4" />
              Сигналы
            </span> */}
            <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 text-xs font-medium text-white/75">
              <Icon name="plug-circle-bold-duotone" className="size-4 text-emerald-300" />
              Коннекторы
            </span>
          </div>
          <p className="min-w-0 truncate font-mono text-2xs text-white/40">
            {scenario.sources.join(" / ")}
          </p>
        </div>
        <AtlasAgentRun
          steps={steps}
          meta={`${scenario.agentSteps.length} шаг(ов) · источники проверены · 12:45`}
        />

        <div className="atlas-result-card mt-4 rounded-2xl border border-white/10 bg-white/[0.035] p-4 sm:ml-12 sm:p-5">
          <div className="flex items-start gap-3 border-b border-white/10 pb-4">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-white text-slate-950">
              <Icon name="document-add-linear" className="size-4" />
            </span>
            <div className="min-w-0">
              <p className="font-mono text-2xs text-white/40">Результат Atlas</p>
              <h4 className="mt-1 text-base font-semibold text-white">
                {scenario.artifact.title}
              </h4>
            </div>
            <span className="ml-auto hidden shrink-0 font-mono text-2xs text-white/40 sm:block">
              {scenario.artifact.updated}
            </span>
          </div>

          <p className="mt-4 text-sm font-medium text-white/75">
            {scenario.artifact.summary}
          </p>

          <ol className="mt-4 grid divide-y divide-white/10 border-y border-white/10">
            {scenario.artifact.rows.map((row, index) => (
              <li
                key={row.label}
                className="grid min-w-0 gap-2 px-3 py-3.5 sm:grid-cols-[7rem_minmax(0,1fr)] sm:items-start"
              >
                <div className="flex min-w-0 items-center gap-2 font-mono text-2xs text-white/40">
                  <span className="text-white/20">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className={`size-1.5 rounded-full ${toneClassNames[row.tone]}`} />
                  <span className="min-w-0 truncate">{row.label}</span>
                </div>
                <div className="min-w-0">
                  <p className="min-w-0 text-xs font-semibold text-white/85 [overflow-wrap:anywhere]">
                    {row.value}
                  </p>
                  <p className="mt-1 min-w-0 text-2xs text-white/40 [overflow-wrap:anywhere]">
                    {row.detail}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-4 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3">
            <span className="flex size-7 items-center justify-center rounded-full bg-white text-slate-950">
              <Icon name="arrow-right-linear" className="size-3.5" />
            </span>
            <div>
              <p className="font-mono text-2xs text-white/40">Следующий шаг</p>
              <p className="mt-0.5 text-xs font-semibold text-white/80">
                {scenario.artifact.nextAction}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="atlas-demo-input-wrap border-t border-white/10 px-4 py-3 sm:px-6 lg:px-7">
        <AtlasInputBar />
      </div>
    </article>
  )
}

function ScenarioCard({
  scenario,
  panelRef,
  isSwitching,
}: {
  scenario: BusinessScenario
  panelRef: RefObject<HTMLDivElement | null>
  isSwitching: boolean
}) {
  return (
    <div
      key={scenario.id}
      ref={panelRef}
      id="business-scenario-panel"
      role="tabpanel"
      aria-labelledby={`${scenario.id}-tab`}
      aria-live="polite"
      tabIndex={0}
      className={`atlas-scenario-panel mt-5 overflow-hidden rounded-[2rem] border border-slate-300 bg-slate-300 shadow-2xl shadow-slate-300/30 outline-none focus-visible:ring-2 focus-visible:ring-violet-500 dark:border-white/15 dark:bg-white/10 dark:shadow-black/30 sm:mt-7 ${
        isSwitching ? "atlas-scenario-panel-exit" : "atlas-scenario-panel-enter"
      }`}
    >
      <div className="grid grid-flow-dense lg:min-h-[43rem] lg:grid-cols-12 lg:gap-px">
        <div className="lg:col-span-5">
          <ScenarioContext scenario={scenario} />
        </div>
        <div className="lg:col-span-7">
          <AtlasRun scenario={scenario} />
        </div>
      </div>
    </div>
  )
}

export default function BusinessScenarios() {
  const [activeIndex, setActiveIndex] = useState(3)
  const [displayedIndex, setDisplayedIndex] = useState(3)
  const [isSwitching, setIsSwitching] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
  const activeIndexRef = useRef(3)
  const switchTimerRef = useRef<number | null>(null)
  const displayedScenario = businessScenarios[displayedIndex]

  useAtlasReveal(sectionRef, scenarioRevealTargets)

  useEffect(() => {
    return () => {
      if (switchTimerRef.current !== null) window.clearTimeout(switchTimerRef.current)
    }
  }, [])

  const selectScenario = (index: number, moveFocus: boolean) => {
    if (index === activeIndexRef.current) return

    const nextScenario = businessScenarios[index]
    if (!nextScenario) return
    activeIndexRef.current = index
    setActiveIndex(index)
    if (moveFocus) tabRefs.current[index]?.focus()
    pushGtmEvent("atlas_use_case_switch", {
      page: window.location.href,
      useCase: nextScenario.id,
      label: nextScenario.role,
    })

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplayedIndex(index)
      return
    }

    if (switchTimerRef.current !== null) window.clearTimeout(switchTimerRef.current)
    setIsSwitching(true)
    switchTimerRef.current = window.setTimeout(() => {
      setDisplayedIndex(index)
      setIsSwitching(false)
      switchTimerRef.current = null
    }, 180)
  }

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (index + 1) % businessScenarios.length
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (index - 1 + businessScenarios.length) % businessScenarios.length
    } else if (event.key === "Home") {
      nextIndex = 0
    } else if (event.key === "End") {
      nextIndex = businessScenarios.length - 1
    }

    if (nextIndex === null) return
    event.preventDefault()
    selectScenario(nextIndex, true)
  }

  return (
    <section
      ref={sectionRef}
      id="scenarios"
      aria-labelledby="scenarios-heading"
      className="relative overflow-x-hidden border-y border-slate-200/70 bg-[#f4f3ef] py-24 dark:border-white/10 dark:bg-[#111317] sm:py-32 lg:py-44"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-[-12rem] top-24 size-[32rem] rounded-full bg-violet-400/10 blur-3xl dark:bg-indigo-500/10"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="scenario-intro max-w-6xl">
          <h2
            id="scenarios-heading"
            className="max-w-6xl text-headline text-slate-950 dark:text-white"
          >
            Узнайте свою задачу. Передайте её Atlas.
          </h2>
          <p className="mt-6 max-w-3xl text-lead text-slate-600 dark:text-white/60">
            Один запрос, разрешённые источники и готовый рабочий артефакт вместо списка
            абстрактных возможностей.
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Сценарии по ролям"
          className="mt-10 flex w-full gap-2 overflow-x-auto rounded-2xl border border-slate-300/80 bg-white/70 p-1.5 shadow-[0_0_0_1px_rgb(0_0_0/0.02)] [scrollbar-width:none] dark:border-white/10 dark:bg-white/[0.04] sm:mt-14 sm:grid sm:grid-cols-4 sm:gap-1 sm:overflow-visible [&::-webkit-scrollbar]:hidden"
        >
          {businessScenarios.map((scenario, index) => {
            const isActive = index === activeIndex
            return (
              <button
                key={scenario.id}
                ref={(node) => {
                  tabRefs.current[index] = node
                }}
                id={`${scenario.id}-tab`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="business-scenario-panel"
                tabIndex={isActive ? 0 : -1}
                onClick={() => selectScenario(index, false)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                className={`group flex min-h-11 min-w-36 shrink-0 items-center justify-center gap-2 rounded-xl px-4 text-xs font-medium transition-[background-color,color,box-shadow] duration-500 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 motion-reduce:transition-none dark:focus-visible:ring-offset-[#111317] sm:min-w-0 sm:px-5 sm:text-sm ${
                  isActive
                    ? "bg-violet-100 text-violet-950 shadow-sm dark:bg-violet-600 dark:text-white"
                    : "text-slate-600 hover:text-slate-950 dark:text-white/55 dark:hover:text-white"
                }`}
              >
                <Icon
                  name={scenario.icon}
                  className="size-4 transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <span className="truncate sm:hidden">{scenario.shortRole}</span>
                <span className="hidden sm:inline">{scenario.role}</span>
              </button>
            )
          })}
        </div>

        <ScenarioCard
          scenario={displayedScenario}
          panelRef={panelRef}
          isSwitching={isSwitching}
        />
      </div>
    </section>
  )
}
